import { useQuery } from '@tanstack/react-query';

import type {
  IseeControllerGetStudentsData,
  IseeSessionTreeDto,
  IseeStudentsResponseDto,
} from './generated';
import {
  iseeControllerGetSessions,
  iseeControllerGetStatus,
  iseeControllerGetStudents,
} from './generated';

export type IseeSessionQuery = IseeControllerGetStudentsData['query'];

/**
 * Feature flag: is the ISEE integration configured on the backend
 * (ISEE_BASE_URL + ISEE_API_KEY both set)? Returns false while loading, so ISEE
 * UI stays hidden until confirmed enabled. Cached for the session.
 */
export function useIseeEnabled(): boolean {
  const { data } = useQuery({
    queryKey: ['isee-status'],
    queryFn: async () => {
      const { data } = await iseeControllerGetStatus({ throwOnError: true });
      return data;
    },
    retry: false,
    staleTime: Infinity,
  });
  return data?.enabled ?? false;
}

export interface IseeSessionsState {
  tree: IseeSessionTreeDto | null;
  probing: boolean;
  available: boolean;
}

/** The sessions running today → +2 months, the tree the batch picker explores. */
export function useIseeSessions(enabled: boolean): IseeSessionsState {
  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ['isee-sessions'],
    queryFn: async () => {
      const { data } = await iseeControllerGetSessions({ throwOnError: true });
      return data;
    },
    enabled,
    retry: false,
    staleTime: 60_000,
  });
  return {
    tree: data ?? null,
    probing: enabled && isLoading,
    available: isSuccess,
  };
}

/** Preview the students of the cohort picked in the tree. */
export async function getIseeStudents(
  query: IseeSessionQuery,
): Promise<IseeStudentsResponseDto> {
  const { data } = await iseeControllerGetStudents({
    query,
    throwOnError: true,
  });
  return data;
}
